import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, Code2, Layers, Zap } from 'lucide-react';
import { projects } from '../data/mock';

const ProjectsSection = () => {
  const [hoveredProject, setHoveredProject] = useState(null);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  const getProjectIcon = (index) => {
    const icons = [Code2, Layers, Zap, Code2, Layers, Zap];
    const Icon = icons[index % icons.length];
    return <Icon size={32} />;
  };

  return (
    <section id="projects" className="relative py-24 bg-black">
      {/* Background effects */}
      <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[150px]" />
      <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] bg-cyan-500/5 rounded-full blur-[100px]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-cyan-400 font-mono text-sm tracking-wider">PORTFOLIO</span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mt-2">Featured Projects</h2>
          <p className="text-gray-500 mt-4 max-w-2xl mx-auto">
            A showcase of my recent work across web development, mobile apps, and enterprise solutions
          </p>
        </motion.div>

        {/* Projects Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {projects.map((project, index) => (
            <motion.div
              key={project.id}
              variants={itemVariants}
              onMouseEnter={() => setHoveredProject(project.id)}
              onMouseLeave={() => setHoveredProject(null)}
              className="group relative"
            >
              <div 
                className="relative h-full p-6 rounded-2xl border border-white/10 bg-white/5 overflow-hidden transition-all duration-500 hover:border-opacity-50"
                style={{ 
                  '--project-color': project.color,
                  borderColor: hoveredProject === project.id ? project.color : undefined
                }}
              >
                {/* Glow effect on hover */}
                <div 
                  className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 rounded-2xl"
                  style={{ backgroundColor: project.color }}
                />
                
                {/* Icon */}
                <div 
                  className="w-16 h-16 rounded-xl flex items-center justify-center mb-4 transition-all duration-300"
                  style={{ 
                    backgroundColor: `${project.color}20`,
                    color: project.color
                  }}
                >
                  {getProjectIcon(index)}
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-semibold text-white mb-2 group-hover:text-cyan-400 transition-colors">
                  {project.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">
                  {project.description}
                </p>
                
                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {project.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 text-xs rounded-full border transition-all duration-300"
                      style={{ 
                        borderColor: `${project.color}50`,
                        color: project.color,
                        backgroundColor: `${project.color}10`
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                
                {/* View link */}
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ 
                    opacity: hoveredProject === project.id ? 1 : 0,
                    y: hoveredProject === project.id ? 0 : 10
                  }}
                  className="flex items-center gap-2 text-sm font-medium transition-colors"
                  style={{ color: project.color }}
                >
                  <span>View Details</span>
                  <ExternalLink size={14} />
                </motion.div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* More projects CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <motion.a
            href="#contact"
            onClick={(e) => {
              e.preventDefault();
              document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center gap-2 px-8 py-3 border border-gray-600 text-gray-300 rounded-full hover:border-cyan-500 hover:text-cyan-400 transition-all duration-200"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span>Want to see more?</span>
            <span className="text-cyan-400">Let's connect</span>
          </motion.a>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsSection;
